<script setup lang="ts">
import type { ColumnDef } from '@tanstack/vue-table'

const genderGroup = useEnumGenderGroup()
const { $ts: t } = useNuxtApp()
const { $api } = useNuxtApp()

const { tournament, onSave, canAddPlayers, group } = defineProps<{
  tournament: Tournament
  onSave: () => void
  canAddPlayers: boolean
  group?: TournamentGroup
}>()

const search = ref<string>('')

const { submit, isSaving, resetForm } = useFormHelper<'/tournaments/{tournament}/create-player', 'post'>({
  initialState: {
    first_name: '',
    last_name: '',
    name: '',
    gender: 'other',
    group_number: group?.group_number,
  },
  afterSave: () => {
    onSave()
    resetForm()
    document.getElementsByName('first_name')?.[0]?.focus()
  },
  method: 'post',
  path: {
    tournament: tournament.id,
  },
  saveEndpoint: '/tournaments/{tournament}/create-player',
  notifyMessage: t('Player created'),
})

function debugAddPlayers(): void {
  $api('/tournaments/{tournament}/add-players-for-testing', {
    path: {
      tournament: tournament.id,
    },
    method: 'post',
  }).then(() => {
    onSave()
    refresh()
  })
}

const page = ref(1)

const {
  data: playerSearchList,
  refresh,
} = await useApi('/players', {
  params: {
    not_in_tournament: tournament.id,
    search: search,
    page: page,
  },
  key: `players-search-${tournament.id}-${search.value}-page-${page.value}`,
})

const playersSearchColumns: ColumnDef<PlayerSearchResult>[] = [
  {
    header: t('Name'),
    accessorKey: 'name',
  },
  {
    header: '',
    accessorKey: 'actions',
  },
]

function addPlayerToTournament(player: PlayerSearchResult) {
  $api('/tournaments/{tournament}/players/attach', {
    path: {
      tournament: tournament.id,
    },
    method: 'post',
    body: {
      action: 'attach',
      player_id: player.id,
      group_number: group?.group_number,
    },
  }).then(() => {
    onSave()
    refresh()
  })
}

watch(search, () => {
  page.value = 1
})
</script>

<template>
  <UiAlert
    v-if="!canAddPlayers"
    :title="$t('Tournament is full')"
  />
  <div
    v-else
    class="flex flex-col gap-4"
  >
    <h3 class="font-bold">
      {{ $t('Create new player') }}
    </h3>
    <UiFormSimple
      autocomplete="off"
      @submit="submit"
    >
      <UiFullInput
        name="first_name"
        :label="$t('First name')"
      />
      <UiFullInput
        name="last_name"
        :label="$t('Last name')"
      />
      <UiFullInput
        name="nickname"
        :label="$t('Nickname')"
      />
      <UiFullSelect
        name="gender"
        :label="$t('Gender')"
      >
        <UiSelectItem
          v-for="gender in genderGroup.gendersAsOptions(false)"
          :key="gender.value"
          :value="gender.value"
        >
          {{ gender.label }}
        </UiSelectItem>
      </UiFullSelect>
      <div class="flex">
        <UiButton
          type="submit"
          class="ml-auto"
          :loading="isSaving"
        >
          {{ $t('Create player') }}
        </UiButton>
      </div>
    </UiFormSimple>
    <UiSeparator />
    <h3 class="font-bold">
      {{ $t('Select existing players') }}
    </h3>
    <UiTableReactiveTable
      :columns="playersSearchColumns"
      :data="playerSearchList?.data ?? []"
    >
      <template #filters>
        <UiInput
          v-model="search"
          name="search"
          :placeholder="$t('Search: Full name/nickname')"
        />
      </template>
      <template #column-name="{ row }">
        <div class="flex flex-col">
          <span>
            <span v-if="row.original.has_dartsspace_account">
              ⭐️
            </span>
            {{ row.original.name }}
          </span>
          <span class="text-gray-500 text-xs">
            {{ row.original.first_name }} {{ row.original.last_name }}
          </span>
        </div>
      </template>
      <template #column-actions="{ row }">
        <UiButton
          icon="tabler-plus"
          size="icon"
          variant="ghost"
          @click="addPlayerToTournament(row.original)"
        />
      </template>
      <template #pagination>
        <Pagination
          v-model:page="page"
          :meta="playerSearchList?.meta"
          without-numbers
        />
      </template>
    </UiTableReactiveTable>
    
  </div>
</template>
